import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SchoolProvider, useSchool } from "./context/SchoolContext";
import { ThemeProvider, useTheme, CLASS_ROUTE_PREFIXES } from "./context/ThemeContext";
import { LiveModeProvider } from "./context/LiveModeContext";
import { TimerProvider } from "./context/TimerContext";

// Marketing pages (new)
import MarketingHomePage from "./pages/marketing/HomePage";
import MarketingLoginPage from "./pages/marketing/LoginPage";
import MarketingSignupPage from "./pages/marketing/SignupPage";
import PrivacyPage from "./pages/marketing/PrivacyPage";

// Existing app pages
import { LoginPage } from "./pages/LoginPage";
import { AuthCallback } from "./pages/AuthCallback";
import { SchoolSelectPage } from "./pages/SchoolSelectPage";
import { Dashboard } from "./pages/Dashboard";
import { StudentsPage } from "./pages/StudentsPage";
import { AttendancePage } from "./pages/AttendancePage";
import { BehaviourPage } from "./pages/BehaviourPage";
import { OutOfClassPage } from "./pages/OutOfClassPage";
import { LessonsPage } from "./pages/LessonsPage";
import { AISummaryPage } from "./pages/AISummaryPage";
import { TimerPage } from "./pages/TimerPage";
import { TeamsPage } from "./pages/TeamsPage";
import { SchoolInfoPage } from "./pages/SchoolInfoPage";
import { WhiteboardPage } from "./pages/WhiteboardPage";
import { TimetablePage } from "./pages/TimetablePage";
import { ClassRedirect } from "./pages/ClassPage";
import { SeatingPlanPage } from "./pages/SeatingPlanPage";
import { CalculatorPage } from "./pages/CalculatorPage";
import { DailyOverviewPage } from "./pages/DailyOverviewPage";
import { StudentPickerPage } from "./pages/StudentPickerPage";
import { NoiseMeterPage } from "./pages/NoiseMeterPage";
import { UpgradePage } from "./pages/UpgradePage";
import { AdminPage } from "./pages/AdminPage";
import { BetaActivatePage } from "./pages/BetaActivatePage";
import { LiveModePage } from "./pages/LiveModePage";
import { SummariesPage } from "./pages/SummariesPage";
import { EarningsPage } from "./pages/EarningsPage";
import { EmergencyWorkPage } from "./pages/EmergencyWorkPage";

// Protected route wrapper - requires authentication
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// School required route - requires school selection
const SchoolRequiredRoute = ({ children }) => {
  const { selectedSchool, loading } = useSchool();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!selectedSchool) {
    return <Navigate to="/app/select-school" state={{ from: location }} replace />;
  }

  return children;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment synchronously during render for session_id
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* ========== MARKETING WEBSITE (Public) ========== */}
      <Route path="/" element={<MarketingHomePage />} />
      <Route path="/features" element={<MarketingHomePage />} />
      <Route path="/pricing" element={<MarketingHomePage />} />
      <Route path="/about" element={<MarketingHomePage />} />
      <Route path="/login" element={<MarketingLoginPage />} />
      <Route path="/signup" element={<MarketingSignupPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      
      {/* ========== RELIEFIFY APP (Protected) ========== */}
      
      {/* Legacy login route - redirects to marketing login */}
      <Route path="/app/login" element={<LoginPage />} />
      
      {/* School selection - first thing after login */}
      <Route path="/app/select-school" element={
        <ProtectedRoute>
          <SchoolSelectPage />
        </ProtectedRoute>
      } />
      
      {/* Main app routes - require both auth AND school selection */}
      <Route path="/app" element={
        <ProtectedRoute>
          <SchoolRequiredRoute>
            <DailyOverviewPage />
          </SchoolRequiredRoute>
        </ProtectedRoute>
      } />
      <Route path="/app/daily-overview" element={
        <ProtectedRoute>
          <SchoolRequiredRoute>
            <DailyOverviewPage />
          </SchoolRequiredRoute>
        </ProtectedRoute>
      } />
      <Route path="/app/dashboard" element={
        <ProtectedRoute>
          <SchoolRequiredRoute>
            <Dashboard />
          </SchoolRequiredRoute>
        </ProtectedRoute>
      } />
      <Route path="/app/live-mode" element={
        <ProtectedRoute>
          <SchoolRequiredRoute>
            <LiveModePage />
          </SchoolRequiredRoute>
        </ProtectedRoute>
      } />
      <Route path="/app/summaries" element={
        <ProtectedRoute>
          <SchoolRequiredRoute>
            <SummariesPage />
          </SchoolRequiredRoute>
        </ProtectedRoute>
      } />
      <Route path="/app/earnings" element={
        <ProtectedRoute>
          <EarningsPage />
        </ProtectedRoute>
      } />
      <Route path="/app/emergency-work" element={
        <ProtectedRoute>
          <EmergencyWorkPage />
        </ProtectedRoute>
      } />
      <Route path="/app/class/:classId" element={<ClassRedirect />} />
      <Route path="/app/students/:classId" element={
        <ProtectedRoute>
          <SchoolRequiredRoute>
            <StudentsPage />
          </SchoolRequiredRoute>
        </ProtectedRoute>
      } />
      <Route path="/app/attendance/:classId" element={
        <ProtectedRoute>
          <SchoolRequiredRoute>
            <AttendancePage />
          </SchoolRequiredRoute>
        </ProtectedRoute>
      } />
      <Route path="/app/behaviour/:classId" element={
        <ProtectedRoute>
          <SchoolRequiredRoute>
            <BehaviourPage />
          </SchoolRequiredRoute>
        </ProtectedRoute>
      } />
      <Route path="/app/out-of-class/:classId" element={
        <ProtectedRoute>
          <SchoolRequiredRoute>
            <OutOfClassPage />
          </SchoolRequiredRoute>
        </ProtectedRoute>
      } />
      <Route path="/app/lessons/:classId" element={
        <ProtectedRoute>
          <SchoolRequiredRoute>
            <LessonsPage />
          </SchoolRequiredRoute>
        </ProtectedRoute>
      } />
      <Route path="/app/ai-summary/:classId" element={
        <ProtectedRoute>
          <SchoolRequiredRoute>
            <AISummaryPage />
          </SchoolRequiredRoute>
        </ProtectedRoute>
      } />
      <Route path="/app/timer" element={
        <ProtectedRoute>
          <SchoolRequiredRoute>
            <TimerPage />
          </SchoolRequiredRoute>
        </ProtectedRoute>
      } />
      <Route path="/app/teams/:classId" element={
        <ProtectedRoute>
          <SchoolRequiredRoute>
            <TeamsPage />
          </SchoolRequiredRoute>
        </ProtectedRoute>
      } />
      <Route path="/app/school-info" element={
        <ProtectedRoute>
          <SchoolRequiredRoute>
            <SchoolInfoPage />
          </SchoolRequiredRoute>
        </ProtectedRoute>
      } />
      <Route path="/app/timetable" element={
        <ProtectedRoute>
          <SchoolRequiredRoute>
            <TimetablePage />
          </SchoolRequiredRoute>
        </ProtectedRoute>
      } />
      <Route path="/app/whiteboard/:classId" element={
        <ProtectedRoute>
          <SchoolRequiredRoute>
            <WhiteboardPage />
          </SchoolRequiredRoute>
        </ProtectedRoute>
      } />
      <Route path="/app/seating-plan/:classId" element={
        <ProtectedRoute>
          <SchoolRequiredRoute>
            <SeatingPlanPage />
          </SchoolRequiredRoute>
        </ProtectedRoute>
      } />
      <Route path="/app/calculator" element={
        <ProtectedRoute>
          <SchoolRequiredRoute>
            <CalculatorPage />
          </SchoolRequiredRoute>
        </ProtectedRoute>
      } />
      <Route path="/app/student-picker/:classId" element={
        <ProtectedRoute>
          <SchoolRequiredRoute>
            <StudentPickerPage />
          </SchoolRequiredRoute>
        </ProtectedRoute>
      } />
      <Route path="/app/noise-meter" element={
        <ProtectedRoute>
          <SchoolRequiredRoute>
            <NoiseMeterPage />
          </SchoolRequiredRoute>
        </ProtectedRoute>
      } />
      <Route path="/app/upgrade" element={
        <ProtectedRoute>
          <UpgradePage />
        </ProtectedRoute>
      } />
      <Route path="/app/upgrade/success" element={
        <ProtectedRoute>
          <UpgradePage />
        </ProtectedRoute>
      } />
      <Route path="/app/admin" element={
        <ProtectedRoute>
          <AdminPage />
        </ProtectedRoute>
      } />
      <Route path="/app/beta-activate" element={
        <ProtectedRoute>
          <BetaActivatePage />
        </ProtectedRoute>
      } />
      
      {/* Fallback - redirect to marketing home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Dynamic toaster that respects dark mode on class pages
const DynamicToaster = () => {
  const { isDark } = useTheme();
  const location = useLocation();
  const appClassPrefixes = CLASS_ROUTE_PREFIXES.map(p => `/app${p}`);
  const isClassRoute = appClassPrefixes.some(p => location.pathname.startsWith(p));
  return <Toaster position="top-right" richColors theme={isDark && isClassRoute ? 'dark' : 'light'} />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SchoolProvider>
          <ThemeProvider>
            <LiveModeProvider>
              <TimerProvider>
                <AppRouter />
                <DynamicToaster />
              </TimerProvider>
            </LiveModeProvider>
          </ThemeProvider>
        </SchoolProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
